import { FC } from 'react';

import { useFormik } from 'formik';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';

import { SlideForm, validationSchema } from '../form';

import { FileSelector } from 'v2/components/fileSystem/fileSelector/FileSelector';
import { Switch } from 'v2/components/switch/Switch';

interface SlideFormDialogProps {
  title: string;
  isOpen: boolean;
  onClose: VoidFunction;
  initialValues: SlideForm;
  isSubmitting: boolean;
  onSubmit: (values: SlideForm, resetForm: VoidFunction) => void;
}

export const SlideFormDialog: FC<SlideFormDialogProps> = ({
  title,
  isOpen,
  onClose,
  initialValues,
  isSubmitting,
  onSubmit,
}) => {
  const formik = useFormik<SlideForm>({
    initialValues,
    validationSchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (values, { resetForm }) => {
      onSubmit(values, resetForm);
    },
  });

  const handleSelectImage = (field: keyof SlideForm) => (fileId: string) => {
    formik.setFieldValue(field, fileId);
  };

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} scroll="paper" maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>

      <DialogContent>
        <form id="slideForm" className="form with-top-space" onSubmit={formik.handleSubmit}>
          <TextField
            name="title"
            value={formik.values.title}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            label="Title"
            required
          />

          <TextField
            name="description"
            value={formik.values.description}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            label="Description"
            multiline
            rows={2}
          />

          <TextField
            name="advantages"
            value={formik.values.advantages}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            label="Advantages"
            multiline
            rows={5}
          />

          <div className="form-group">
            <TextField
              name="buttonText"
              value={formik.values.buttonText}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label="Button Text"
              required
              fullWidth
            />

            <TextField
              name="link"
              value={formik.values.link}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label="Button Link"
              required
              fullWidth
            />

            <TextField
              name="ymId"
              value={formik.values.ymId}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label="Metric Id"
              required
              fullWidth
            />
          </div>

          <div className="form-group">
            <Switch name="isActive" checked={formik.values.isActive} onChange={formik.handleChange} label="Active" />

            <Switch
              name="isOrderFixed"
              checked={formik.values.isOrderFixed}
              onChange={formik.handleChange}
              label="Fixed Order"
            />
          </div>

          <div className="form-group">
            <FileSelector
              value={formik.values.backgroundImageId}
              onSelect={handleSelectImage('backgroundImageId')}
              error={formik.errors.backgroundImageId}
              label="Background"
              required
            />

            <FileSelector
              value={formik.values.buttonImageId}
              onSelect={handleSelectImage('buttonImageId')}
              error={formik.errors.buttonImageId}
              label="Button"
              required
            />
          </div>
        </form>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="error" variant="outlined">
          Cancel
        </Button>

        <Button form="slideForm" type="submit" variant="contained" loading={isSubmitting}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
